import { useEffect, useRef, useState } from 'preact/hooks';
import { AppIcon, type IconName } from '../AppIcon';
import { QuestionSelect } from '../QuestionSelect';
import { Tooltip } from '../Tooltip';
import { describePdfSource, PDF_FIELD_TYPE_NAMES, samePdfSource, type PdfBindingEvent } from '../../../shared/pdfBindingValidation';
import type { PdfDocumentDefinition, PdfPlacement, PdfValueSource } from '../../../shared/types';
import { retargetRequirements } from '../../lib/pdfEditor';
import { answerOptions, NO_ANSWERS, pdfAnswerChoices } from '../../lib/pdfAnswerChoices';
import { SIGNING_DATES } from '../../lib/pdfSources';

export const PDF_FIELD_ICONS: Record<PdfPlacement['kind'], IconName> = { text: 'field-text', checkbox: 'field-checkbox', signature: 'field-signature', date: 'field-date' };
/** How far the pointer moves, in pixels, before a press on a row's number becomes a drag. */
const DRAG_START = 4;
/** The fields with the one at `from` moved to `to`. */
export const moved = <T,>(items: T[], from: number, to: number) => { const next = [...items]; next.splice(to, 0, next.splice(from, 1)[0]); return next; };
const SIGNERS: { label: string; source: PdfValueSource }[] = [
  { label: 'Participant signature', source: { kind: 'signature', role: 'participant' } },
  { label: 'Parent or guardian signature', source: { kind: 'signature', role: 'parent' } },
];

interface Props {
  event: PdfBindingEvent; document: PdfDocumentDefinition;
  /** The fields selected on the PDF, whose rows are marked. */
  selected: ReadonlySet<string>;
  /** Fields that need attention, shown red as on the PDF. */
  attention: ReadonlySet<string>;
  onSelect: (placement: PdfPlacement) => void;
  onChange: (document: PdfDocumentDefinition) => void;
  /** Deletes this field, as Delete does on the PDF. */
  onDelete: (placement: PdfPlacement) => void;
  /** Saves the fields in a new order, which renumbers them. */
  onReorder: (placements: PdfPlacement[]) => void;
}

/** Every field on the form, below the PDF, in order: its number in a solid circle as on the PDF, its type's symbol, and
 * what it shows. A text field or checkbox shows its bound question in a dropdown, a signature field who signs, and a
 * date field the date signed. Changing one fills the field at once, its number selects it on the PDF, and the trash
 * can at the end of its row deletes it. Dragging a row by its number and type symbol, or Alt and an up or down arrow on
 * them, moves the field in the order; the numbers change once it's dropped. */
export function PdfFieldList({ event, document, selected, attention, onSelect, onChange, onDelete, onReorder }: Props) {
  const choices = { text: pdfAnswerChoices(event, document, 'text'), checkbox: pdfAnswerChoices(event, document, 'checkbox') };
  // A new answer keeps any requirement on the old one with the field, as choosing it in the field's settings does.
  const fill = (placement: PdfPlacement, source: PdfValueSource) => {
    if (samePdfSource(placement.source, source)) return;
    onChange({ ...document, placements: document.placements.map(p => p.id === placement.id ? { ...p, source } : p),
      requiredSources: placement.source.kind === 'unset' ? document.requiredSources : retargetRequirements(document, [placement], placement.source, source) });
  };
  // The list scrolls within its own area: a field selected on the PDF brings its row into view there, moving nothing else.
  const list = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const row = list.current?.querySelector<HTMLElement>('.pdf-field-row.selected'), box = list.current?.parentElement;
    if (!row || !box) return;
    const top = row.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop, bottom = top + row.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top - 4; else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight + 4;
  }, [[...selected].join(), document.placements.length]);
  // A press on a row's number, and once it has moved far enough, the drag: where each row sat when it began, where the
  // pointer is now, and the order it would make if dropped there.
  const placements = document.placements;
  const press = useRef<{ pointerId: number; from: number; y: number; tops: number[]; heights: number[]; dragging: boolean } | undefined>(undefined);
  const [drag, setDrag] = useState<{ from: number; to: number; dy: number }>();
  const dragged = useRef(false);
  const order = drag ? moved(placements, drag.from, drag.to) : placements;
  /** Where the pointer is down the list's content, which may be scrolled. */
  const along = (e: PointerEvent) => e.clientY - list.current!.getBoundingClientRect().top;
  const begin = (e: PointerEvent, index: number) => {
    if (e.button !== 0 || placements.length < 2) return;
    const rows = [...list.current!.querySelectorAll<HTMLElement>(':scope > .pdf-field-row')];
    press.current = { pointerId: e.pointerId, from: index, y: along(e), tops: rows.map(r => r.offsetTop), heights: rows.map(r => r.offsetHeight), dragging: false };
  };
  // The list holds the pointer during a drag, since the row being dragged moves within it as the order changes.
  const follow = (e: PointerEvent) => {
    const p = press.current; if (!p || e.pointerId !== p.pointerId) return;
    const y = along(e);
    if (!p.dragging) {
      if (Math.abs(y - p.y) < DRAG_START) return;
      p.dragging = true; list.current!.setPointerCapture?.(e.pointerId);
    }
    // Near the top or bottom of the list's area, it scrolls to bring more rows into reach.
    const box = list.current!.parentElement!, area = box.getBoundingClientRect();
    if (e.clientY < area.top + 24) box.scrollTop -= 8; else if (e.clientY > area.bottom - 24) box.scrollTop += 8;
    // The row goes before the first other row whose middle the pointer hasn't reached.
    const centre = p.tops[p.from] + (y - p.y) + p.heights[p.from] / 2;
    const to = p.tops.filter((top, i) => i !== p.from && top + p.heights[i] / 2 < centre).length;
    // It follows the pointer, measured from the place it now has in the order.
    const top = to <= p.from ? p.tops[to] : p.tops[to] + p.heights[to] - p.heights[p.from];
    setDrag({ from: p.from, to, dy: y - p.y - (top - p.tops[p.from]) });
  };
  const end = (e: PointerEvent, cancel = false) => {
    const p = press.current; if (!p || e.pointerId !== p.pointerId) return;
    press.current = undefined; setDrag(undefined);
    if (!p.dragging) return;
    // The press was a drag, not a click, so it doesn't also select.
    dragged.current = true; setTimeout(() => { dragged.current = false; }, 0);
    if (cancel || !drag || drag.to === drag.from) return;
    onReorder(moved(placements, drag.from, drag.to)); onSelect(placements[drag.from]);
  };
  return <ol ref={list} class={`pdf-field-list${drag ? ' reordering' : ''}`} aria-label="Fields on this PDF"
    onPointerMove={follow} onPointerUp={e => end(e)} onPointerCancel={e => end(e, true)} onLostPointerCapture={e => end(e, true)}>
    {order.map(p => {
      // While a row is dragged the others make room, but every number keeps its old value until it's dropped.
      const index = placements.indexOf(p), number = index + 1, label = `Field ${number}`, moving = !!drag && placements[drag.from].id === p.id;
      return <li key={p.id} class={`pdf-field-row${selected.has(p.id) ? ' selected' : ''}${attention.has(p.id) ? ' needs-attention' : ''}${moving ? ' dragging' : ''}`}
        style={moving ? { transform: `translateY(${drag!.dy}px)` } : undefined}>
        <button type="button" class="pdf-field-row-select" aria-label={`Select ${label} on the PDF`} aria-pressed={selected.has(p.id)} aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
          onClick={() => { if (!dragged.current) onSelect(p); }} onPointerDown={e => begin(e, index)}
          onKeyDown={e => {
            // Alt and an arrow moves this field one place in the order, keeping focus on its row.
            if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
            e.preventDefault(); const to = index + (e.key === 'ArrowUp' ? -1 : 1);
            if (to < 0 || to >= placements.length) return;
            onReorder(moved(placements, index, to));
            requestAnimationFrame(() => list.current?.querySelectorAll<HTMLElement>('.pdf-field-row-select')[to]?.focus());
          }}>
          <span class="pdf-field-row-badge" aria-hidden="true">{number}</span>
          <span class="pdf-field-row-type" title={PDF_FIELD_TYPE_NAMES[p.kind]}><AppIcon name={PDF_FIELD_ICONS[p.kind]} /><span class="sr-only">{PDF_FIELD_TYPE_NAMES[p.kind]}</span></span>
        </button>
        <div class="pdf-field-row-answer">{(() => {
          if (p.kind === 'text' || p.kind === 'checkbox') {
            const list = choices[p.kind], at = list.findIndex(c => samePdfSource(c.source, p.source));
            return <QuestionSelect label={`Question for ${label}`} menuWidth="trigger" value={at >= 0 ? String(at) : ''} placeholder="Choose a question" options={answerOptions(list)} empty={NO_ANSWERS[p.kind]} onChange={value => fill(p, list[Number(value)].source)} />;
          }
          if (p.kind === 'signature') {
            const at = SIGNERS.findIndex(s => samePdfSource(s.source, p.source));
            return <QuestionSelect label={`Signature type for ${label}`} menuWidth="trigger" value={at >= 0 ? String(at) : ''} placeholder="Choose a signature type"
              options={SIGNERS.map((s, i) => ({ value: String(i), label: s.label }))} onChange={value => fill(p, SIGNERS[Number(value)].source)} />;
          }
          // A date field shows the date signed or a parent or guardian's; one saved with another date says which.
          const date = SIGNING_DATES.findIndex(d => samePdfSource(d.source, p.source));
          if (date >= 0) return <QuestionSelect label={`Date for ${label}`} menuWidth="trigger" value={String(date)} placeholder="Choose a date"
            options={SIGNING_DATES.map((d, i) => ({ value: String(i), label: d.label }))} onChange={value => fill(p, SIGNING_DATES[Number(value)].source)} />;
          return <span class="pdf-field-row-text">{describePdfSource(document, p.source, event)}</span>;
        })()}</div>
        <Tooltip text="Delete field"><button type="button" class="secondary icon-button pdf-field-row-delete" aria-label={`Delete ${label}`} onClick={() => onDelete(p)}><AppIcon name="trash" /></button></Tooltip>
      </li>;
    })}
  </ol>;
}
